<template>
  <v-row class="text-center">
    <v-col cols="10" md="4" sm="8" offset-md="4" offset-sm="2" offset="1">
      <v-img :src="upperLogo" alt="Welcome to Harlequin" class="mx-auto" />
      <v-card>
        <v-toolbar
          color="#f87723"
          dark
        >{{ $t('SETTINGS.CHANGE_PASSWORD') }}</v-toolbar>
        <v-card-text>
          <v-form>
            <v-row v-if="reset && !ssoDetails.msalEnabled && !ssoDetails.samlEnabled">
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="password1"
                  :label="$t('ADMIN.USERS.FIELDS.PASS')"
                  :append-icon="showPass ? 'mdi-eye-off' : 'mdi-eye'"
                  :type="showPass ? 'text' : 'password'"
                  @click:append="showPass = !showPass"
                  :rules="[rules.password]"
                  autocomplete="new-password"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  onpaste="return false"
                  v-model="password2"
                  :label="$t('ADMIN.USERS.FIELDS.CONFIRM_PASS')"
                  :append-icon="showPass2 ? 'mdi-eye-off' : 'mdi-eye'"
                  :type="showPass2 ? 'text' : 'password'"
                  @click:append="showPass2 = !showPass2"
                  :rules="password1 != '' ? [rules.required, rules.password, rules.passMatch] : []"
                  autocomplete="new-password"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row v-if="!reset || ssoDetails.msalEnabled || ssoDetails.samlEnabled">
              <div class="mt-2">{{ $t('GENERAL.PW_RESET_NO') }}</div>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-col class="text-center">
            <v-btn
              v-if="reset && !ssoDetails.msalEnabled && !ssoDetails.samlEnabled"
              large
              dark
              color="#f87723"
              @click="changePw({password1, password2})"
            >{{ $t('SETTINGS.CHANGE_PASSWORD') }}</v-btn>
          </v-col>
        </v-card-actions>

         <v-snackbar
          v-model="snack"
          :timeout="3000"
          :color="snackColor"
        >
          {{ snackText }}

          <template v-slot:action="{ attrs }">
            <v-btn
              v-bind="attrs"
              text
              @click="snack = false"
            >
              Close
            </v-btn>
          </template>
        </v-snackbar>
      </v-card>
      <v-img :src="lowerLogo" alt="Welcome to Harlequin" class="mx-auto rsl-logo" />
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex';
export default {
    name: 'changepw',
    computed: mapGetters(['ssoDetails', 'resetUid']),
    data: function() {
        return {
          upperLogo: '',
          lowerLogo: '',
          passwordPolicy: '',
          password1: null,
          password2: null,
          showPass: false,
          showPass2: false,
          msalEnabled: false,
          samlEnabled: false,
          ldapEnabled: false,
          reset: false,
          userId: '',
          rules: {
            required: value => !!value || 'Required',
            password: value => {
              if(value != '') {
                const pattern = /^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9])(?=.*?[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~])[a-zA-Z0-9!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]{10,}$/
                return pattern.test(value) || 'Passwords must meet the following criteria: Min 10 characters with at least 1 uppercase letter, 1 lowercase letter, 1 digit and 1 special character.'
              }else {
                return true;
              }
            },
            passMatch: value => value === this.password1 && value === this.password2 || 'The passwords do not match'
          },
          snack: false,
          snackColor: '',
          snackText: ''
        }
    },
    created() {
      this.unsubscribe = this.$store.subscribe((mutation) => {

        // Listen for UID
        if(mutation.type === 'RESET_UID') {
          if(this.resetUid == 'key_error') {
            this.snackError('This reset link is no longer valid');
          }else {
            this.userId = this.resetUid;
          }
        }

        // Listen for update response
        if(mutation.type === 'UPDATED_USER_CREDS') {
          // Remove the key
          this.$store.dispatch('removeLookupKey', this.key);
          // Redirect to login
          this.$router.push({ name: 'Login' });
        }

      });

      this.$store.dispatch('getSsoDetails');

      if(this.$route.query.reset) {
        this.reset = true;
        this.key = this.$route.query.key;
      }
    },
    beforeDestroy() {
      this.unsubscribe();
    },
    mounted() {
      // Set logos
      this.upperLogo = process.env.VUE_APP_UPPER_IMAGE;
      this.lowerLogo = process.env.VUE_APP_LOWER_IMAGE;

      // Add negative tabindex to icons
      let icons = document.querySelectorAll('.v-icon');
      
      for(let v = 0; v < icons.length; v++) {
        icons[v].setAttribute('tabindex', -1);
      }

      // Unique key lookup
      this.$store.dispatch('getLookupKey', this.key);
    },
    methods: {
      async changePw(item) {
        if(this.userId != '' && item.password1 != '') {
          let credPayload = {
            'userId': this.userId,
            'credData': {
              'password': item.password1,
              'authenticatedUserPassword': process.env.VUE_APP_ENGINE_API_PASS
            }
          };
          await this.$store.dispatch('updateUserCreds', credPayload).then( () => {
            this.updateLog('Admin', 'Updated', 'User credentials updated for ' + this.userId + '.');
          });
        }
      },
      updateLog(cat, op, msg) {
        let dt = new Date();
        let dateTime = new Intl.DateTimeFormat('en-GB', 
          {
            day: 'numeric',
            month: 'numeric', 
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: false
          }).format(dt);

        let logData = {
          'category': cat,
          'userId': this.username,
          'caseId': 0,
          'processName': null,
          'taskName': null,
          'operation': op,
          'msg': msg,
          'dateTime': dateTime,
        };
        this.$store.dispatch('updateOpLog', logData);
      },
      snackError(msg) {
        this.snack = true;
        this.snackTimeout = 10000;
        this.snackColor = 'error';
        this.snackText = msg;
      }
    }
}
</script>

<style>

</style>